/**
 * The official Wall-E mark, in the two shapes the console uses.
 *
 * Both come from the derivations in `assets/`, which come from the archived
 * source in the programme workspace. The asset file stays the single source of
 * the glyph: nothing here repeats its path data.
 *
 * The file is imported as text and inlined into the document, which is what
 * makes `fill="currentColor"` mean our colour. The two alternatives both
 * failed for the same underlying reason, that an SVG has to be part of this
 * document to read anything from it:
 *
 *   - `<img src=...>` renders the SVG as an isolated document, so its
 *     `currentColor` resolves against its own default and the mark comes out
 *     near-black on a dark background.
 *   - A CSS mask would have worked, but React drops `maskImage` from the style
 *     prop and Griffel drops it from a class, in both cases silently. What
 *     rendered was a solid brand-coloured rectangle the shape of the logo's
 *     box, with no logo in it.
 *
 * The favicon is the third derivation and is not here. It sits on the
 * browser's tab strip where no token reaches, so it carries a fixed grey of
 * its own (see `assets/favicon.svg`).
 */

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import fullSvg from './assets/logo-full.svg?raw'
import markSvg from './assets/logo-mark.svg?raw'

const useStyles = makeStyles({
  glyph: {
    display: 'block',
    flexShrink: 0,
    lineHeight: 0,
    color: tokens.colorBrandForeground1,
    '& svg': {
      display: 'block',
      height: '100%',
      width: 'auto',
    },
  },
})

/** The hexagon alone. For the collapsed rail, where there is no room for more. */
export function ConsoleMark({ size = 24 }: { size?: number }) {
  const styles = useStyles()
  return (
    <span
      className={styles.glyph}
      style={{ height: size }}
      // Our own build-time asset, not anything a request can reach.
      dangerouslySetInnerHTML={{ __html: markSvg }}
    />
  )
}

/** Hexagon and wordmark. For the expanded rail and the sign-in panel. */
export function ConsoleLogo({ height = 24, className }: { height?: number; className?: string }) {
  const styles = useStyles()
  return (
    <span
      className={mergeClasses(styles.glyph, className)}
      style={{ height }}
      dangerouslySetInnerHTML={{ __html: fullSvg }}
    />
  )
}
